/* SM v45 — bag variant editing. The original is retained until its replacement
 * is verified. Shopify can partially add inventory on a 422 response, so every
 * write is reconciled against the cart before any further write.
 * Reference: https://shopify.dev/docs/api/ajax/reference/cart */
export class CartEditError extends Error {
  constructor(message, review = false) {
    super(message);
    this.review = review;
  }
}
const REVIEW = 'We could not confirm the change. Please review your bag before continuing.';
const propertiesKey = (properties) => JSON.stringify(Object.entries(properties || {}).sort(([a], [b]) => a.localeCompare(b)));
const planId = (item) => item.selling_plan_allocation?.selling_plan?.id || null;
const signature = (item) => JSON.stringify([Number(item.variant_id || item.id), propertiesKey(item.properties), planId(item)]);
const matching = (cart, model) => cart.items.filter((item) => signature(item) === signature(model));
const quantity = (items) => items.reduce((total, item) => total + item.quantity, 0);
const unsupported = (item) => Boolean(planId(item) || item.item_components?.length || item.parent_relationship);

export function createCartTransport(root = '/') {
  const base = root.endsWith('/') ? root : `${root}/`;
  return async (type, body) => {
    const response = await fetch(`${base}cart${type === 'get' ? '' : `/${type}`}.js`, {
      method: type === 'get' ? 'GET' : 'POST',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      ...(type === 'get' ? {} : { body: JSON.stringify(body) }),
    });
    return { ok: response.ok, status: response.status, body: await response.json() };
  };
}

export async function replaceCartVariant(transport, selection, variantId) {
  const read = async () => {
    let result;
    try { result = await transport('get'); } catch (_) { throw new CartEditError(REVIEW, true); }
    if (!result.ok || !Array.isArray(result.body?.items)) throw new CartEditError(REVIEW, true);
    return result.body;
  };
  const write = async (type, body) => {
    // Never retry an ambiguous write: it may already have reached Shopify.
    try { return await transport(type, body); } catch (_) { return { ok: false, status: 0 }; }
  };
  const baseline = await read();
  const original = baseline.items.find((item) => item.key === selection.key);
  if (!original || original.quantity !== selection.quantity || Number(original.product_id) !== Number(selection.productId)) {
    throw new CartEditError('Your bag has changed. Close this panel and choose Edit again.', true);
  }
  if (unsupported(original) || matching(baseline, original).length !== 1) {
    throw new CartEditError('Please view this piece to change its options.');
  }
  const selected = selection.variants.find((variant) => Number(variant.id) === Number(variantId));
  if (!selected?.available) throw new CartEditError('This option is unavailable. Please choose another.');
  if (Number(original.variant_id || original.id) === Number(variantId)) return { cart: baseline, changed: false };

  const replacement = { ...original, id: Number(variantId), variant_id: Number(variantId) };
  const previousTargetQuantity = quantity(matching(baseline, replacement));
  const expectedTargetQuantity = previousTargetQuantity + original.quantity;
  const originalIntact = (cart) => quantity(matching(cart, original)) === original.quantity;
  const targetQuantity = (cart) => quantity(matching(cart, replacement));

  // Roll back only a verified increase belonging to this variant + exact
  // properties. Unrelated lines and existing destination quantities stay intact.
  const rollbackReplacement = async (cart) => {
    let remaining = targetQuantity(cart) - previousTargetQuantity;
    if (!originalIntact(cart) || remaining < 0 || remaining > original.quantity) throw new CartEditError(REVIEW, true);
    for (let attempt = 0; remaining > 0 && attempt < 20; attempt += 1) {
      const line = matching(cart, replacement).at(-1);
      if (!line || unsupported(line)) throw new CartEditError(REVIEW, true);
      const reduction = Math.min(remaining, line.quantity);
      await write('change', { id: line.key, quantity: line.quantity - reduction });
      const updated = await read();
      const nextRemaining = targetQuantity(updated) - previousTargetQuantity;
      if (!originalIntact(updated) || nextRemaining !== remaining - reduction) throw new CartEditError(REVIEW, true);
      cart = updated;
      remaining = nextRemaining;
    }
    if (remaining !== 0) throw new CartEditError(REVIEW, true);
    return cart;
  };

  const added = await write('add', { items: [{ id: Number(variantId), quantity: original.quantity, properties: original.properties || {} }] });
  let cart = await read();
  if (!added.ok || targetQuantity(cart) !== expectedTargetQuantity || !originalIntact(cart)) {
    cart = await rollbackReplacement(cart);
    const detail = added.body?.description;
    throw new CartEditError(typeof detail === 'string' ? `${detail} Your original selection is still in your bag.` : 'We could not update this option. Your original selection is still in your bag.');
  }

  // Automatic discounts can change line keys. Use the fresh, unambiguous key.
  const originals = matching(cart, original);
  if (originals.length !== 1 || unsupported(originals[0])) {
    await rollbackReplacement(cart);
    throw new CartEditError('Please view this piece to change its options. Your original selection is still in your bag.');
  }
  await write('change', { id: originals[0].key, quantity: 0 });
  cart = await read();
  if (matching(cart, original).length === 0 && targetQuantity(cart) === expectedTargetQuantity) {
    return { cart, changed: true };
  }
  if (originalIntact(cart)) {
    await rollbackReplacement(cart);
    throw new CartEditError('The change could not be completed. Your original selection is still in your bag.');
  }
  throw new CartEditError(REVIEW, true);
}

if (typeof document !== 'undefined' && !window.smCartEditInstalled) {
  window.smCartEditInstalled = true;
  let dialog;
  let opener;
  let selection;
  let pending = false;
  let reviewRequired = false;
  let recoveryLocation;
  let live;

  function ensureDialog() {
    if (dialog) return;
    live = document.createElement('div');
    live.className = 'visually-hidden';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    document.body.append(live);
    dialog = document.createElement('dialog');
    dialog.className = 'sm-cart-edit-dialog';
    dialog.setAttribute('aria-labelledby', 'SMCartEditTitle');
    dialog.innerHTML = '<button class="sm-cart-edit-close" type="button" aria-label="Close edit options">×</button><p class="sm-cart-edit-kicker">Your selection</p><h2 id="SMCartEditTitle"></h2><form><label for="SMCartEditVariant"></label><select id="SMCartEditVariant" name="variant" required aria-describedby="SMCartEditStatus"></select><p id="SMCartEditStatus" class="sm-cart-edit-status" role="status" aria-live="polite"></p><button class="sm-cart-edit-submit" type="submit">Update selection</button></form><a class="sm-cart-edit-product">View this piece</a>';
    document.body.append(dialog);
    dialog.querySelector('.sm-cart-edit-close').addEventListener('click', close);
    dialog.addEventListener('cancel', (event) => { event.preventDefault(); close(); });
    dialog.addEventListener('keydown', (event) => { if (event.key === 'Escape') event.stopPropagation(); });
    dialog.querySelector('form').addEventListener('submit', submit);
    dialog.querySelector('select').addEventListener('change', () => {
      status('');
      dialog.querySelector('[type="submit"]').disabled = Number(dialog.querySelector('select').value) === Number(selection.variantId);
    });
    dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('sm-cart-editor-open');
      if (opener?.isConnected) opener.focus();
      else {
        const container = document.querySelector(`coretex-cart[data-sm-cart-location="${recoveryLocation}"]`);
        const next = container?.querySelector('[data-sm-cart-edit]') || container;
        if (next) { if (!next.hasAttribute('tabindex')) next.setAttribute('tabindex', '-1'); next.focus({ preventScroll: true }); }
      }
    });
  }
  function status(text, error = false) {
    const target = dialog.querySelector('.sm-cart-edit-status');
    target.textContent = text;
    target.toggleAttribute('data-error', error);
  }
  function busy(value) {
    pending = value;
    dialog.setAttribute('aria-busy', String(value));
    dialog.querySelectorAll('button, select').forEach((element) => { element.disabled = value; });
    const productLink = dialog.querySelector('.sm-cart-edit-product');
    if (value) { productLink.removeAttribute('href'); productLink.setAttribute('aria-disabled', 'true'); }
    else {
      productLink.href = reviewRequired ? (window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`) : selection.url;
      productLink.textContent = reviewRequired ? 'Review your bag' : 'View this piece';
      productLink.removeAttribute('aria-disabled');
    }
    if (!value) dialog.querySelector('[type="submit"]').disabled = reviewRequired || Number(dialog.querySelector('select').value) === Number(selection.variantId);
  }
  function close() {
    if (pending) return;
    dialog.close();
    if (reviewRequired) window.location.assign(window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
  }
  function refresh() {
    return new Promise((resolve) => {
      const ajax = window.liquidAjaxCart;
      if (!ajax?.update) return resolve(false);
      // A failed renderer callback must not leave the shopper trapped in a
      // disabled modal. The verified mutation can still be reviewed on /cart.
      let settled = false;
      const finish = (value) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        resolve(value);
      };
      const timeout = setTimeout(() => finish(false), 12000);
      try {
        ajax.update({}, { info: { initiator: dialog }, lastCallback: (state) => {
          const response = state?.responseData;
          const sections = response?.body?.sections;
          finish(Boolean(response?.ok && sections && Object.values(sections).every((html) => typeof html === 'string')));
        } });
      } catch (_) { finish(false); }
    });
  }
  async function submit(event) {
    event.preventDefault();
    if (pending || reviewRequired) return;
    if (window.liquidAjaxCart?.processing) { status('Your bag is updating. Please try again in a moment.'); return; }
    busy(true);
    status('Updating your selection…');
    try {
      await replaceCartVariant(createCartTransport(window.Shopify?.routes?.root || '/'), selection, dialog.querySelector('select').value);
      const refreshed = await refresh();
      if (!refreshed) { reviewRequired = true; status('Your selection has been updated. Close this panel to view your bag.'); }
      else { live.textContent = 'Your selection has been updated.'; busy(false); dialog.close(); }
    } catch (error) {
      reviewRequired = Boolean(error.review);
      const refreshed = await refresh();
      if (!refreshed) reviewRequired = true;
      status(error instanceof CartEditError ? error.message : REVIEW, true);
      if (!(error instanceof CartEditError)) reviewRequired = true;
    } finally {
      busy(false);
      if (dialog.open) dialog.querySelector('.sm-cart-edit-close').focus();
    }
  }
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-sm-cart-edit]');
    if (!trigger || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
    if (!window.liquidAjaxCart?.init || typeof HTMLDialogElement === 'undefined') return;
    event.preventDefault();
    if (pending || window.liquidAjaxCart.processing) return;
    let data;
    try { data = JSON.parse(trigger.dataset.smCartEdit); } catch (_) { window.location.assign(trigger.href); return; }
    ensureDialog();
    opener = trigger;
    recoveryLocation = trigger.closest('coretex-cart')?.dataset.smCartLocation || 'page';
    selection = data;
    reviewRequired = false;
    dialog.querySelector('h2').textContent = data.title;
    dialog.querySelector('label').textContent = data.optionLabel;
    dialog.querySelector('.sm-cart-edit-product').href = trigger.href;
    const select = dialog.querySelector('select');
    select.replaceChildren(...data.variants.map((variant) => {
      const option = document.createElement('option');
      option.value = variant.id;
      option.textContent = `${variant.title} — ${variant.price}${variant.available ? '' : ' — Unavailable'}`;
      option.disabled = !variant.available;
      option.selected = Number(variant.id) === Number(data.variantId);
      return option;
    }));
    status('Your quantity will stay the same.');
    busy(false);
    dialog.showModal();
    document.documentElement.classList.add('sm-cart-editor-open');
    select.focus();
  });
}
